import {
    useCallback,
    useMemo, useState,
} from 'react'
import FormWithControls from 'app/shared-components/FormWithControls'
import {
    AccountLane,
} from 'app/Apps/AccountManagement/AccountLanes/AccountLanes.types'
import {
    isDeliveryRoad, showFTL, showLTL, isCollectionOnly,
} from 'app/Apps/Pricing/utils/utils'
import useFieldsWithClassNames from 'app/hooks/useFieldsWithClassName'
import useEnumValues from 'app/hooks/useEnumValues'
import {
    RequestConfig,
} from 'app/types/request.types'
import useHasPermission from 'app/hooks/useHasPermission'
import {
    CollectionServiceType, DeliveryServiceType, LaneStatus,
} from 'app/enums/label.enums'
import {
    addressCategoryValidation, isProspectAndNotShippingLocation,
    addressMainCategoryValidation,
} from 'app/Apps/ContactManagement/Locations'
import {
    isOneOfContactsProspect, isContactRequired,
} from 'app/Apps/ContactManagement/Contacts/Contacts.validation'
import {
    Contact,
} from 'app/Apps/ContactManagement/Contacts/Contacts.types'
import {
    SkyNetLinkContent,
} from 'app/shared-components/SkyNetLink'
import {
    ActionLinkFunction,
} from 'app/types/form.types'
import UpdateProspectContactPopup from 'app/shared-components/UpdateProspectContactPopup'
import useCheckAwardedLanes from '../hooks/useCheckAwardedLanes'
import getFields from './AwardRejectLaneForm.fields'
import useStyles from './AwardRejectLaneForm.styles'

const defaultProps = {
    getContactManagementLink: undefined,
    getEditHandoverAddressLink: undefined,
    getEditCollectionAddressLink: undefined,
    showMainActionButtons: true,
}

export default function AwardRejectLaneForm({
    data,
    disabled,
    onSuccess,
    onCancel,
    requestParam,
    getCustomButtons,
    getContactManagementLink,
    getEditHandoverAddressLink,
    getEditCollectionAddressLink,
    showMainActionButtons,
}: Readonly<{
    data: AccountLane,
    requestParam: RequestConfig,
    disabled?: boolean,
    onSuccess: (...args: any[]) => void,
    onCancel: (...args: any[]) => void,
    getCustomButtons: (...a: any[]) => JSX.Element[],
    getContactManagementLink?: () => JSX.Element[],
    getEditHandoverAddressLink?: ActionLinkFunction,
    getEditCollectionAddressLink?: ActionLinkFunction,
    showMainActionButtons?: boolean,
}>) {
    const {
        classes,
    } = useStyles()
    const getAllowedValues = useEnumValues()
    const accountUpdatePermission = useHasPermission(['account_update_all'])
    const [
        value,
        setValue,
    ] = useState(data)
    const [
        prospectContacts,
        setProspectContacts,
    ] = useState<Contact[]>([])

    const [
        showUpdateProspectContact,
        setShowUpdateProspectContact,
    ] = useState<boolean>(false)

    const hasAwardedLanes = useCheckAwardedLanes(value?.laneGroupId)
    const contractBasisAdminPermission = useHasPermission(['contract_basis_admin'])
    const isCollection = value?.collectionServiceType === CollectionServiceType.COLLECTION
    const collectionDropoffPointValidation = useMemo(() => {
        if (value?.collectionServiceType === CollectionServiceType.DROPOFF_SKYCELL) {
            return addressCategoryValidation
        }
        if (isCollection) {
            return addressMainCategoryValidation
        }

        return isProspectAndNotShippingLocation
    }, [
        value?.collectionServiceType,
        isCollection,
    ])

    const handoverPointContactsValidate = useMemo(() => {
        if (isDeliveryRoad(value?.deliveryServiceType)) {
            return [
                isContactRequired,
                isOneOfContactsProspect,
            ]
        }
        return isOneOfContactsProspect
    }, [value?.deliveryServiceType])

    const collectionPointContactValidate = useMemo(() => {
        if (isCollectionOnly(value?.collectionServiceType)) {
            return [
                isContactRequired,
                isOneOfContactsProspect,
            ]
        }
        return isOneOfContactsProspect
    }, [value?.collectionServiceType])

    const getEditProspectContactLink = useCallback((contacts: Contact[] = []) => {
        if (accountUpdatePermission) {
            const errors = isOneOfContactsProspect(contacts)

            if (Array.isArray(errors) && errors?.length && value?.collectionDropoffPoint?.id) {
                setProspectContacts(contacts?.filter(({
                    isProspect,
                }) => {
                    return isProspect
                }))
                return (
                    [(
                        <SkyNetLinkContent
                            key="skynet-link-update-prospect"
                            className={classes.actionLink}
                            onClick={() => {
                                setShowUpdateProspectContact(true)
                            }}
                            text="Update Prospect"
                        />
                    )]
                )
            }

            return []
        }
        return []
    }, [
        accountUpdatePermission,
        classes.actionLink,
        value?.collectionDropoffPoint?.id,
    ])

    const fields = useFieldsWithClassNames(
        getFields({
            getAllowedValues,
            isDeliveryRoad: isDeliveryRoad(value?.deliveryServiceType),
            isCollectionOnly:
                isCollectionOnly(value?.collectionServiceType),
            isCollection,
            handoverPointContactsValidate,
            collectionPointContactValidate,
            showFTL: showFTL(value?.transportModeRoad),
            showLTL: showLTL(value?.transportModeRoad),
            hasAwardedLanes,
            contractBasisAdminPermission,
            handoverPointValidation: value?.deliveryServiceType === DeliveryServiceType.PICKUP
                ? addressCategoryValidation
                : isProspectAndNotShippingLocation,
            collectionDropoffPointValidation,
            getContactManagementLink,
            getEditHandoverAddressLink,
            getEditCollectionAddressLink,
            getEditProspectContactLink,
            disabled: disabled || [
                LaneStatus.AWARDED,
                LaneStatus.IMPLEMENTED,
            ].includes(data?.laneStatus),
        }), classes,
    )

    const formValues = useMemo(() => {
        return {
            ...data,
            ...value,
        }
    }, [
        data,
        value,
    ])

    return (
        <div className={classes.formContainer}>
            <FormWithControls
                disabled={disabled}
                value={formValues}
                name="AccountLane"
                requestParams={requestParam}
                setExternalValue={setValue}
                fields={fields}
                onSuccess={onSuccess}
                onCancel={onCancel}
                classNames={{
                    gridWrapper: classes.gridWrapper,
                }}
                exists={Boolean(data?.id)}
                getCustomButtons={getCustomButtons}
                canCancel={!disabled && showMainActionButtons}
                canUpdate={!disabled && showMainActionButtons}
            />
            <UpdateProspectContactPopup
                data={{
                    location: value?.collectionDropoffPoint,
                    contacts: prospectContacts,
                }}
                open={showUpdateProspectContact}
                close={() => {
                    setShowUpdateProspectContact(false)
                }}
            />
        </div>
    )
}

AwardRejectLaneForm.defaultProps = defaultProps
